/* import __COLOCATED_TEMPLATE__ from './main.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */

import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import type { UserField } from 'embercom/objects/standalone/data-fields/zendesk-user-field';
import { StandaloneUserAttribute } from 'embercom/objects/standalone/data-fields/common';
import { ATTRIBUTE_TYPES_TO_HIDE } from 'embercom/components/new-settings/data/people/attributes';
import type ExternalFieldIdentifier from 'embercom/models/standalone/external-field-identifier';
import type Attribute from 'embercom/models/attribute';

interface Args {
  archivedAttributes: $TSFixMe[];
}

type AttributeWithSettings = { attribute: Attribute };

export default class PeopleAttributesMain extends Component<Args> {
  @service declare modalService: $TSFixMe;
  @service declare attributeService: $TSFixMe;
  @service declare finStandaloneService: FinStandaloneService;
  @service declare appService: AppService;

  get attributes() {
    return this.unmappedStandaloneUserAttributes
      .map(
        (attributeWithSettings: AttributeWithSettings) =>
          new StandaloneUserAttribute({
            intercomAttribute: attributeWithSettings.attribute,
          }),
      )
      .concat(
        this.standaloneSyncedUserFields.map(
          (userField: UserField | ExternalFieldIdentifier) =>
            new StandaloneUserAttribute({
              standalonePlatform: this.standalonePlatform.platformKey,
              userField,
              intercomAttribute: this.displayableUserAttributes.find(
                (attributeWithSettings: AttributeWithSettings) =>
                  userField.intercomAttributeIdentifier ===
                  attributeWithSettings.attribute.identifier,
              )!.attribute,
            }),
        ),
        this.standaloneBuiltInUserFields.map(
          (userField: UserField | ExternalFieldIdentifier) =>
            new StandaloneUserAttribute({
              standalonePlatform: this.standalonePlatform.platformKey,
              userField,
            }),
        ),
      );
  }

  private get unmappedStandaloneUserAttributes() {
    return this.displayableUserAttributes
      .filter(
        (attributeWithSettings: AttributeWithSettings) =>
          !this.standalonePlatformUserFields.any(
            (field) =>
              field.intercomAttributeIdentifier === attributeWithSettings.attribute.identifier,
          ),
      )
      .concat(this.archivedUserAttributes);
  }

  private get displayableUserAttributes(): AttributeWithSettings[] {
    return this.attributeService.allUserAttributes
      .filter((setting: any) => !ATTRIBUTE_TYPES_TO_HIDE.includes(setting.attribute.type))
      .concat(this.attributeService.userRelationshipDisplayableAttributes);
  }

  private get standaloneSyncedUserFields(): (ExternalFieldIdentifier | UserField)[] {
    return this.standalonePlatformUserFields.filter(
      (field) => field.synchronized && !field.builtIn,
    );
  }

  private get standaloneBuiltInUserFields(): (ExternalFieldIdentifier | UserField)[] {
    return this.standalonePlatformUserFields.filter((field) => field.builtIn);
  }

  private get standalonePlatformUserFields() {
    return this.standalonePlatform.userFields;
  }

  private get standalonePlatform() {
    return this.appService.mainStandalonePlatform;
  }

  private get archivedUserAttributes() {
    return this.archivedAttributesWithSettings.filterBy('attribute.isUser');
  }

  private get archivedAttributesWithSettings() {
    return this.attributeService.attributesWithSettings(this.args.archivedAttributes);
  }

  @action
  openAttributeCreationModal() {
    this.modalService.openModal('settings/modals/attribute-details', {
      attribute: null,
      allowArchival: false,
      showPeopleAndCompanyToggleTab: false,
      typeToCreate: 'people',
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::DataAttributes::People::Main': typeof PeopleAttributesMain;
    'standalone/people-attributes/main': typeof PeopleAttributesMain;
  }
}
